import React, { useState, useRef, useEffect } from "react";
import { Select, Button, message } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PrinterOutlined,
  DownloadOutlined,
  BgColorsOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { canWrite } from "../../helpers/permissions";
import "./index.css";

// Logo from public folder
const logoImage = "/rapsap-loyal.svg";

const { Option, OptGroup } = Select;

// Process custom template HTML with item data and images
// Handles both Grocery and Cafe CSV formats
const processCustomTemplate = (template, item, images = {}) => {
  let html = template;

  // Detect format: Cafe CSV has "PRODUCT NAME", Grocery has "Item Name"
  const isCafe = "PRODUCT NAME" in item;

  const itemName = isCafe ? (item["PRODUCT NAME"] || "") : (item["Item Name"] || "");
  const mrp = parseFloat(item.MRP) || 0;
  const price = isCafe ? (parseFloat(item.LYP) || 0) : (parseFloat(item["Rapsap Price"]) || 0);
  const savings = mrp - price;
  const savingsPercent = mrp > 0 ? Math.round((savings / mrp) * 100) : 0;

  // Dynamic font size for long item names to prevent overflow
  // Cafe names are uppercase so they take more width — use lower thresholds
  const longThreshold = isCafe ? 30 : 45;
  const medThreshold = isCafe ? 22 : 35;
  let nameHtml = itemName;
  if (itemName.length > longThreshold) {
    nameHtml = `<span style="font-size: 0.7em">${itemName}</span>`;
  } else if (itemName.length > medThreshold) {
    nameHtml = `<span style="font-size: 0.85em">${itemName}</span>`;
  }

  // Common variables (work for both formats)
  html = html.replace(/\{\{itemName\}\}/g, nameHtml);
  html = html.replace(/\{\{mrp\}\}/g, Math.round(mrp));
  html = html.replace(/\{\{price\}\}/g, Math.round(price));
  html = html.replace(/\{\{savings\}\}/g, Math.round(savings));
  html = html.replace(/\{\{savingsPercent\}\}/g, savingsPercent);

  // Grocery-specific variables
  html = html.replace(/\{\{brand\}\}/g, item.Brand || "");
  html = html.replace(/\{\{category\}\}/g, item.Categories || "");

  // Cafe-specific variables
  html = html.replace(/\{\{loyalPrice\}\}/g, Math.round(parseFloat(item.LYP) || 0));
  html = html.replace(/\{\{calories\}\}/g, item.CALS || "");
  html = html.replace(/\{\{vegNonVeg\}\}/g, item["VEG / NON"] || "");
  const isVeg = (item["VEG / NON"] || "").toUpperCase() === "VEG";
  html = html.replace(/\{\{vegColor\}\}/g, isVeg ? "#22BD03" : "#EF4444");
  html = html.replace(/\{\{vegLabel\}\}/g, isVeg ? "VEG" : "NON-VEG");

  // Veg/Non-veg icon HTML with inline styles
  const vegIconHtml = isVeg
    ? `<div style="width:32px;height:32px;border-radius:4px;border:3px solid #22BD03;display:flex;align-items:center;justify-content:center;"><div style="width:16px;height:16px;background:#22BD03;border-radius:9999px;"></div></div>`
    : `<div style="width:32px;height:32px;border-radius:4px;border:3px solid #EF4444;display:flex;align-items:center;justify-content:center;"><div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-bottom:14px solid #EF4444;"></div></div>`;
  html = html.replace(/\{\{vegIcon\}\}/g, vegIconHtml);

  // Calories badge HTML
  const calsValue = item.CALS || "";
  const calsBadgeHtml = calsValue
    ? `<div style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f5f5f5;border-radius:12px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;color:#666;"><span>${calsValue}</span><span style="font-size:11px;">cal</span></div>`
    : "";
  html = html.replace(/\{\{caloriesBadge\}\}/g, calsBadgeHtml);

  // Replace image placeholders
  html = html.replace(/\{\{image1\}\}/g, images.image1 || "/wow-wednesday-logo.png");
  html = html.replace(/\{\{image2\}\}/g, images.image2 || "/rapsap-loyal-logo.png");
  html = html.replace(/\{\{image3\}\}/g, images.image3 || "");
  html = html.replace(/\{\{image4\}\}/g, images.image4 || "");

  return html;
};

// Custom Card component for saved templates
// Auto-injects cafe elements (veg icon, calories) when cafe data is detected
const CustomCard = ({ item, template }) => {
  const processedHtml = processCustomTemplate(template.html, item, template.images || {});
  const isCafe = "PRODUCT NAME" in item;

  if (isCafe) {
    const hasVegIcon = template.html.includes("{{vegIcon}}");
    const hasCalBadge = template.html.includes("{{caloriesBadge}}");
    const isVeg = (item["VEG / NON"] || "").toUpperCase() === "VEG";
    const vegColor = isVeg ? "#22BD03" : "#EF4444";
    const calories = item.CALS || "";
    const needsOverlay = (!hasVegIcon || (!hasCalBadge && calories));

    if (needsOverlay) {
      return (
        <div style={{ position: "relative", display: "inline-block" }}>
          <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
          {/* Veg/Non-veg + Calories overlay strip at bottom-left */}
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
              zIndex: 10,
            }}
          >
            {!hasVegIcon && (
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 4,
                  border: `2.5px solid ${vegColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "white",
                  flexShrink: 0,
                }}
              >
                {isVeg ? (
                  <div style={{ width: 12, height: 12, background: vegColor, borderRadius: "9999px" }} />
                ) : (
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      borderBottom: `10px solid ${vegColor}`,
                    }}
                  />
                )}
              </div>
            )}
            {!hasCalBadge && calories && (
              <div
                style={{
                  padding: "2px 8px",
                  background: "rgba(255,255,255,0.92)",
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#666",
                  fontFamily: "'DM Sans', sans-serif",
                  border: "1px solid #e5e7eb",
                }}
              >
                {calories} cal
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  return <div dangerouslySetInnerHTML={{ __html: processedHtml }} />;
};

// Sample data loaded on mount
const SAMPLE_DATA = [
  {
    Categories: "Snacks",
    Brand: "Lay's",
    "Item Name": "American Style Cream And Onion Potato Chips",
    MRP: "20",
    "Rapsap Price": "16",
    Savings: "7%",
  },
  {
    Categories: "Beverage",
    Brand: "Pepsi",
    "Item Name": "Cola Soft Drink Bottle",
    MRP: "40",
    "Rapsap Price": "35",
    Savings: "12%",
  },
  {
    Categories: "Essentials",
    Brand: "Tata",
    "Item Name": "Premium Tea Gold Pack 500g",
    MRP: "250",
    "Rapsap Price": "210",
    Savings: "15%",
  },
];

// Cafe sample data for cafe mode (matches menu_items.csv structure)
const CAFE_SAMPLE_DATA = [
  {
    "SR NO": "1",
    "PRODUCT NAME": "BAKE CHEESE PASTRY",
    "MRP": "199",
    "LYP": "169",
    "CALS": "450",
    "VEG / NON": "VEG",
  },
  {
    "SR NO": "2",
    "PRODUCT NAME": "TIRAMISU CUP PASTRY",
    "MRP": "199",
    "LYP": "169",
    "CALS": "550",
    "VEG / NON": "VEG",
  },
  {
    "SR NO": "3",
    "PRODUCT NAME": "HAZELNUT PRALINE PASTRY",
    "MRP": "149",
    "LYP": "119",
    "CALS": "480",
    "VEG / NON": "VEG",
  },
  {
    "SR NO": "4",
    "PRODUCT NAME": "CHICKEN KHEEMA ROLL",
    "MRP": "79",
    "LYP": "59",
    "CALS": "650",
    "VEG / NON": "NON",
  },
  {
    "SR NO": "5",
    "PRODUCT NAME": "PANEER TIKKA SANDWICH",
    "MRP": "159",
    "LYP": "109",
    "CALS": "550",
    "VEG / NON": "VEG",
  },
  {
    "SR NO": "6",
    "PRODUCT NAME": "BBQ CHAR-GRILLED CHICKEN SANDWICH",
    "MRP": "169",
    "LYP": "119",
    "CALS": "600",
    "VEG / NON": "NON",
  },
];

// Cafe card theme presets for seasonal/festival theming
const CAFE_THEMES = {
  default: {
    id: "default",
    name: "Default",
    cardBackground: "#ffffff",
    cardBorder: "#e5e7eb",
    leftSectionBg: "#ffffff",
    itemNameColor: "#1a1a1a",
    regularPriceColor: "#000000",
    loyalLabelColor: "#4D6FE9",
    loyalPriceColor: "#4D6FE9",
    rightSectionBg: "#F3F3F3",
    rightSectionBorderLeft: "rgba(0, 0, 0, 0.14)",
    cafeLabelColor: "#000000",
    loyalButtonGradient: ["#4F72F0", "#2F4591"],
    loyalSubtextColor: "#ffffff",
    bannerText: "",
    bannerBg: "#4D6FE9",
    bannerTextColor: "#ffffff",
    decorations: [],
  },
  valentines: {
    id: "valentines",
    name: "Valentine's Day",
    cardBackground: "#FFF0F3",
    cardBorder: "#F9A8D4",
    leftSectionBg: "#FFF0F3",
    itemNameColor: "#9D174D",
    regularPriceColor: "#BE185D",
    loyalLabelColor: "#E11D48",
    loyalPriceColor: "#E11D48",
    rightSectionBg: "#FCE7F3",
    rightSectionBorderLeft: "#F9A8D4",
    cafeLabelColor: "#9D174D",
    loyalButtonGradient: ["#E11D48", "#9D174D"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Valentine's Special",
    bannerBg: "#E11D48",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\u2764\uFE0F", position: { top: 4, left: 4 }, size: 18, opacity: 0.7, rotation: -15 },
      { type: "emoji", content: "\uD83D\uDC95", position: { bottom: 40, left: 8 }, size: 14, opacity: 0.5, rotation: 10 },
    ],
  },
  holi: {
    id: "holi",
    name: "Holi",
    cardBackground: "#FFFBEB",
    cardBorder: "#F59E0B",
    leftSectionBg: "#FFFBEB",
    itemNameColor: "#7C3AED",
    regularPriceColor: "#D97706",
    loyalLabelColor: "#7C3AED",
    loyalPriceColor: "#7C3AED",
    rightSectionBg: "#FEF3C7",
    rightSectionBorderLeft: "#F59E0B",
    cafeLabelColor: "#D97706",
    loyalButtonGradient: ["#7C3AED", "#5B21B6"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Happy Holi!",
    bannerBg: "#7C3AED",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\uD83C\uDFA8", position: { top: 4, right: 155 }, size: 16, opacity: 0.7, rotation: 15 },
      { type: "emoji", content: "\uD83C\uDF08", position: { top: 4, left: 4 }, size: 16, opacity: 0.6, rotation: -10 },
    ],
  },
  diwali: {
    id: "diwali",
    name: "Diwali",
    cardBackground: "#FDF4E7",
    cardBorder: "#D97706",
    leftSectionBg: "#FDF4E7",
    itemNameColor: "#92400E",
    regularPriceColor: "#B45309",
    loyalLabelColor: "#D97706",
    loyalPriceColor: "#D97706",
    rightSectionBg: "#FEF3C7",
    rightSectionBorderLeft: "#D97706",
    cafeLabelColor: "#92400E",
    loyalButtonGradient: ["#D97706", "#92400E"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Diwali Special",
    bannerBg: "#D97706",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\uD83E\uDEAF", position: { top: 4, left: 4 }, size: 18, opacity: 0.8, rotation: 0 },
      { type: "emoji", content: "\u2728", position: { bottom: 40, left: 8 }, size: 14, opacity: 0.6, rotation: 0 },
    ],
  },
  christmas: {
    id: "christmas",
    name: "Christmas",
    cardBackground: "#FEF2F2",
    cardBorder: "#16A34A",
    leftSectionBg: "#FEF2F2",
    itemNameColor: "#166534",
    regularPriceColor: "#DC2626",
    loyalLabelColor: "#DC2626",
    loyalPriceColor: "#DC2626",
    rightSectionBg: "#F0FDF4",
    rightSectionBorderLeft: "#16A34A",
    cafeLabelColor: "#166534",
    loyalButtonGradient: ["#DC2626", "#991B1B"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Merry Christmas!",
    bannerBg: "#16A34A",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\uD83C\uDF84", position: { top: 4, left: 4 }, size: 18, opacity: 0.7, rotation: 0 },
      { type: "emoji", content: "\u2B50", position: { top: 4, right: 155 }, size: 14, opacity: 0.6, rotation: 15 },
    ],
  },
  eid: {
    id: "eid",
    name: "Eid",
    cardBackground: "#F0FDF9",
    cardBorder: "#059669",
    leftSectionBg: "#F0FDF9",
    itemNameColor: "#065F46",
    regularPriceColor: "#047857",
    loyalLabelColor: "#059669",
    loyalPriceColor: "#059669",
    rightSectionBg: "#ECFDF5",
    rightSectionBorderLeft: "#059669",
    cafeLabelColor: "#065F46",
    loyalButtonGradient: ["#059669", "#065F46"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Eid Mubarak!",
    bannerBg: "#059669",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\uD83C\uDF19", position: { top: 4, left: 4 }, size: 18, opacity: 0.7, rotation: -10 },
      { type: "emoji", content: "\u2B50", position: { bottom: 40, left: 8 }, size: 14, opacity: 0.5, rotation: 15 },
    ],
  },
  independence: {
    id: "independence",
    name: "Independence Day",
    cardBackground: "#FFF7ED",
    cardBorder: "#EA580C",
    leftSectionBg: "#FFF7ED",
    itemNameColor: "#1E3A5F",
    regularPriceColor: "#EA580C",
    loyalLabelColor: "#15803D",
    loyalPriceColor: "#15803D",
    rightSectionBg: "#F0FDF4",
    rightSectionBorderLeft: "#EA580C",
    cafeLabelColor: "#1E3A5F",
    loyalButtonGradient: ["#EA580C", "#9A3412"],
    loyalSubtextColor: "#ffffff",
    bannerText: "Jai Hind!",
    bannerBg: "#1E3A5F",
    bannerTextColor: "#ffffff",
    decorations: [
      { type: "emoji", content: "\uD83C\uDDEE\uD83C\uDDF3", position: { top: 4, left: 4 }, size: 18, opacity: 0.8, rotation: 0 },
    ],
  },
  newyear: {
    id: "newyear",
    name: "New Year",
    cardBackground: "#1E1B4B",
    cardBorder: "#F59E0B",
    leftSectionBg: "#1E1B4B",
    itemNameColor: "#FDE68A",
    regularPriceColor: "#F59E0B",
    loyalLabelColor: "#F59E0B",
    loyalPriceColor: "#F59E0B",
    rightSectionBg: "#312E81",
    rightSectionBorderLeft: "#F59E0B",
    cafeLabelColor: "#FDE68A",
    loyalButtonGradient: ["#F59E0B", "#D97706"],
    loyalSubtextColor: "#1E1B4B",
    loyalLogoFilter: "brightness(0)",
    bannerText: "Happy New Year!",
    bannerBg: "#F59E0B",
    bannerTextColor: "#1E1B4B",
    decorations: [
      { type: "emoji", content: "\uD83C\uDF86", position: { top: 4, left: 4 }, size: 18, opacity: 0.8, rotation: 0 },
      { type: "emoji", content: "\uD83E\uDD42", position: { bottom: 40, left: 8 }, size: 14, opacity: 0.6, rotation: -10 },
    ],
  },
};

// Parse CSV text to array of objects
const parseCSV = (text) => {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^["']|["']$/g, ""));
  const items = [];

  for (let i = 1; i < lines.length; i++) {
    const values = [];
    let current = "";
    let inQuotes = false;

    for (const char of lines[i]) {
      if (char === '"' || char === "'") {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    if (values.length >= headers.length) {
      const item = {};
      headers.forEach((header, index) => {
        item[header] = values[index] || "";
      });
      items.push(item);
    }
  }

  return items;
};

// Compact Card Template (269x113px)
const CompactCard = ({ item }) => {
  const itemName = item["Item Name"] || "";
  const price = Math.round(parseFloat(item["Rapsap Price"]) || 0);
  const mrp = Math.round(parseFloat(item.MRP) || 0);
  const rupeeSavings = mrp - price;

  return (
    <div className="shelf-card-compact">
      {/* Item Name */}
      <div className="compact-item-name">{itemName}</div>

      {/* Savings Section - Right Side */}
      <div className="compact-savings-section">
        <div className="compact-savings-row">
          <span className="compact-rupee-symbol">₹</span>
          <span className="compact-savings-amount">{rupeeSavings}</span>
        </div>
        <div className="compact-savings-text">savings</div>
      </div>

      {/* Price Row - Bottom Left */}
      <div className="compact-price-row">
        <span className="compact-our-price">Our price</span>
        <span className="compact-our-price-value">₹{price}</span>
        <span className="compact-mrp">₹{mrp}</span>
      </div>

      {/* Blue Bar */}
      <div className="compact-blue-bar">
        <img src="/rapsap-loyal.svg" alt="Rapsap Loyal" className="compact-loyal-logo" />
        <div className="compact-additional">
          <span className="additional-percent">+7%</span>
          <span className="additional-text">Additional savings</span>
        </div>
      </div>
    </div>
  );
};

// Logo paths from public folder
const wowWednesdayLogo = "/wow-wednesday-logo.png";
const rapsapLoyalLogo = "/rapsap-loyal-logo.png";

// WOW Wednesday Card Template (500x200px - same as Medium Card)
const WowWednesdayCard = ({ item }) => {
  const itemName = item["Item Name"] || "";
  const price = Math.round(parseFloat(item["Rapsap Price"]) || 0);
  const mrp = Math.round(parseFloat(item.MRP) || 0);
  const rupeeSavings = mrp - price;

  return (
    <div className="shelf-card-wow">
      {/* Left Section */}
      <div className="wow-left-section">
        {/* Item Name - Full Width */}
        <div className="wow-item-name">{itemName}</div>

        {/* Price Row */}
        <div className="wow-price-row">
          <span className="wow-our-price">Our Price: ₹{price}</span>
          <span className="wow-mrp">MRP ₹{mrp}</span>
        </div>

        {/* Large Savings Display */}
        <div className="wow-savings-display">
          <span className="wow-rupee-symbol">₹</span>
          <span className="wow-savings-amount">{rupeeSavings}</span>
          <span className="wow-savings-text">savings</span>
        </div>
      </div>

      {/* Right Section */}
      <div className="wow-right-section">
        {/* WOW Wednesday Logo */}
        <img
          src={wowWednesdayLogo}
          alt="WOW Wednesday"
          className="wow-wednesday-logo"
        />

        {/* Blue Gradient Bar with Loyal Logo */}
        <div className="wow-blue-bar">
          <img
            src={rapsapLoyalLogo}
            alt="Rapsap Loyal"
            className="wow-loyal-logo"
          />
          <div className="wow-divider"></div>
          <div className="wow-additional-section">
            <span className="wow-percent">+7%</span>
            <span className="wow-additional-text">additional savings</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Large Card Template (500x200px)
const LargeCard = ({ item }) => {
  const itemName = item["Item Name"] || "";
  const mrp = Math.round(parseFloat(item.MRP) || 0);
  const rapsapPrice = Math.round(parseFloat(item["Rapsap Price"]) || 0);
  const rupeeSavings = mrp - rapsapPrice;

  return (
    <div className="shelf-card-large">
      <div className="large-left-section">
        <div className="large-top-row">
          <div className="large-item-name">{itemName}</div>
          <div className="large-price-section">
            <div className="large-current-price">₹{rapsapPrice}</div>
            <div className="large-mrp-price">₹{mrp}</div>
          </div>
        </div>
        <div className="large-savings-display">
          <span className="large-rupee-savings">₹{rupeeSavings}</span>
          <span className="large-savings-text">savings</span>
        </div>
      </div>
      <div className="large-right-section">
        <img src={logoImage} alt="Rapsap" className="large-logo" />
        <div className="large-additional-savings">
          <span className="large-percent">7%</span>
          <span className="large-additional-text">additional savings</span>
        </div>
      </div>
    </div>
  );
};

// Cafe Card Template (500x200px) - medium size for cafe items
// Matches CSV structure: SR NO, PRODUCT NAME, MRP, LYP, CALS, VEG / NON
// Accepts optional theme prop for seasonal/festival theming
const CafeCard = ({ item, theme, offerValue }) => {
  const t = theme || CAFE_THEMES.default;
  const itemName = item["PRODUCT NAME"] || "";
  const typeValue = (item["VEG / NON"] || "VEG").toUpperCase();
  const isVeg = typeValue === "VEG";
  const mrp = Math.round(parseFloat(item["MRP"]) || 0);
  const loyalPrice = Math.round(parseFloat(item["LYP"]) || 0);

  return (
    <div className="cafe-card" style={{
      background: t.cardBackground,
      borderColor: t.cardBorder,
    }}>
      {/* Theme decorations */}
      {t.decorations && t.decorations.map((d, i) => (
        <div key={i} style={{
          position: 'absolute',
          ...d.position,
          fontSize: d.size,
          opacity: d.opacity,
          transform: `rotate(${d.rotation || 0}deg)`,
          zIndex: 5,
          pointerEvents: 'none',
          lineHeight: 1,
        }}>
          {d.content}
        </div>
      ))}

      {/* Left Section */}
      <div className="cafe-left-section" style={{ background: t.leftSectionBg }}>
        <div className="cafe-item-name" style={{ color: t.itemNameColor }}>{itemName}</div>
        <div className="cafe-regular-price" style={{ color: t.regularPriceColor }}>Regular Price : ₹{mrp}</div>
        <div className="cafe-loyal-row">
          <span className="cafe-loyal-label" style={{ color: t.loyalLabelColor }}>Loyal Price</span>
          <div className="cafe-loyal-price">
            <span className="cafe-rupee" style={{ color: t.loyalPriceColor }}>₹</span>
            <span className="cafe-price-value" style={{ color: t.loyalPriceColor }}>{loyalPrice}</span>
          </div>
        </div>
      </div>

      {/* Veg/Non-veg Indicator */}
      <div className={`cafe-veg-indicator ${isVeg ? 'veg' : 'non-veg'}`}>
        {isVeg ? (
          <div className="veg-circle"></div>
        ) : (
          <div className="non-veg-triangle"></div>
        )}
      </div>

      {/* Right Section */}
      <div className="cafe-right-section" style={{
        background: t.rightSectionBg,
        borderLeftColor: t.rightSectionBorderLeft,
      }}>
        {/* Theme banner */}
        {t.bannerText && (
          <div style={{
            background: t.bannerBg,
            color: t.bannerTextColor,
            padding: '4px 8px',
            fontSize: 11,
            fontWeight: 700,
            fontFamily: "'Barlow Condensed', sans-serif",
            fontStyle: 'italic',
            textAlign: 'center',
            margin: '-16px -12px 6px -12px',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}>
            {t.bannerText}
          </div>
        )}

        {/* Rapsap Logo */}
        <div className="cafe-brand-section">
          <img src="/rapsap-black-logo.png" alt="Rapsap" className="cafe-rapsap-logo" />
          <div className="cafe-label">
            <img src="/coffee-icon.svg" alt="" className="cafe-coffee-icon" />
            <span style={{ color: t.cafeLabelColor }}>cafe</span>
          </div>
        </div>

        {/* Loyal Button with Rapsap Loyal logo */}
        <div className="cafe-loyal-button" style={{
          background: `linear-gradient(180deg, ${t.loyalButtonGradient[0]} 0%, ${t.loyalButtonGradient[1]} 100%)`,
        }}>
          <img src="/rapsap-loyal.svg" alt="Loyal" className="cafe-loyal-logo" style={{ filter: t.loyalLogoFilter || 'none' }} />
          <span className="loyal-subtext" style={{ color: t.loyalSubtextColor }}>{offerValue ? `+${offerValue} ` : ''}additional savings</span>
        </div>
      </div>
    </div>
  );
};

export default function ShelfTalker() {
  const [mode, setMode] = useState("Grocery"); // "Grocery" | "Cafe"
  const [items, setItems] = useState(SAMPLE_DATA);
  const [template, setTemplate] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const fileInputRef = useRef(null);
  const printAreaRef = useRef(null);

  // Cafe theme state
  const [cafeThemeId, setCafeThemeId] = useState("default");
  const [cafeThemeCustom, setCafeThemeCustom] = useState(null);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [cafeOfferValue, setCafeOfferValue] = useState("");

  // Load saved templates from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("shelfTalkerTemplates");
    if (saved) {
      setSavedTemplates(JSON.parse(saved));
    }
  }, []);

  // Load cafe theme from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("cafeCardTheme");
      if (saved) {
        const { id, custom, offerValue } = JSON.parse(saved);
        setCafeThemeId(id || "default");
        setCafeThemeCustom(custom || null);
        if (offerValue) setCafeOfferValue(offerValue);
      }
    } catch (e) { /* ignore parse errors */ }
  }, []);

  const saveCafeTheme = (id, custom, offer) => {
    localStorage.setItem("cafeCardTheme", JSON.stringify({ id, custom, offerValue: offer !== undefined ? offer : cafeOfferValue }));
  };

  const handleCafeThemeSelect = (id) => {
    setCafeThemeId(id);
    setCafeThemeCustom(null);
    saveCafeTheme(id, null);
  };

  const handleCafeThemeCustomize = (prop, value) => {
    const base = cafeThemeCustom || CAFE_THEMES[cafeThemeId];
    const updated = { ...base, [prop]: value };
    setCafeThemeCustom(updated);
    saveCafeTheme(cafeThemeId, updated);
  };

  const handleResetTheme = () => {
    setCafeThemeId("default");
    setCafeThemeCustom(null);
    saveCafeTheme("default", null);
  };

  // Handle mode change - reset items and template
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setItems(newMode === "Cafe" ? CAFE_SAMPLE_DATA : SAMPLE_DATA);
    setTemplate(newMode === "Cafe" ? "cafe-card" : null);
  };

  // Handle CSV file upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === "string") {
        const parsed = parseCSV(text);
        if (parsed.length > 0) {
          setItems(parsed);
          message.success(`Loaded ${parsed.length} items from CSV`);
        } else {
          message.error("No valid items found in CSV");
        }
      }
    };
    reader.readAsText(file);

    // Reset file input for re-uploading
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Print handler - opens A4 preview in new window with exact same styles
  const handlePrint = () => {
    if (!printAreaRef.current) return;

    const isCompact = template === "template1";
    const isWow = template === "template3";
    const isCafe = mode === "Cafe" || template === "cafe-card";
    const isCustom = template?.startsWith("custom-");
    const customTemplate = isCustom ? savedTemplates.find(t => `custom-${t.id}` === template) : null;
    const printContent = printAreaRef.current.innerHTML;
    const printWindow = window.open('', '_blank');

    // Generate filename with current date (ST-DD-MM-YYYY or CAFE-DD-MM-YYYY)
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const filename = isCafe ? `CAFE-${day}-${month}-${year}` : `ST-${day}-${month}-${year}`;

    // A4 dimensions: 210mm x 297mm = 794px x 1123px at 96dpi (with 6mm padding = ~745px x 1078px usable)
    // Determine card dimensions based on template type
    let cardWidth, cardHeight;
    if (isCafe) {
      cardWidth = 500;
      cardHeight = 200;
    } else if (isCompact) {
      cardWidth = 269;
      cardHeight = 113;
    } else if (customTemplate) {
      cardWidth = customTemplate.width || 500;
      cardHeight = customTemplate.height || 200;
    } else {
      // Large/WOW cards
      cardWidth = 500;
      cardHeight = 200;
    }

    // Calculate optimal layout for any card size
    // A4 usable area: ~745px x 1078px (after 6mm padding)
    const a4Width = 745;
    const a4Height = 1078;
    const gap = 6;

    // Calculate how many cards fit in normal orientation
    const normalCols = Math.floor((a4Width + gap) / (cardWidth + gap));
    const normalRows = Math.floor((a4Height + gap) / (cardHeight + gap));
    const normalCardsMax = normalCols * normalRows;

    // Calculate remaining space for rotated cards
    const normalAreaWidth = normalCols * cardWidth + (normalCols - 1) * gap;
    const remainingWidth = a4Width - normalAreaWidth - gap;

    // Rotated card: width becomes height, height becomes width
    const rotatedFitsWidth = remainingWidth >= cardHeight;
    const rotatedRows = rotatedFitsWidth ? Math.floor((a4Height + gap) / (cardWidth + gap)) : 0;

    // For compact cards, we can fit 2 columns normal + 3 rotated
    // For large cards, we can fit 1 column normal (5 cards) + 2 rotated
    // For cafe cards (270x114), same as compact: 2 cols x 9 rows normal + 3 rotated
    let normalCardsPerPage, rotatedCardsPerPage;
    if (cardWidth <= 300 && cardHeight <= 150) {
      // Compact-like (including cafe): 2 cols x 9 rows normal + 3 rotated
      normalCardsPerPage = 18;
      rotatedCardsPerPage = 3;
    } else {
      // Large-like: 1 col x 5 rows normal + 2 rotated
      normalCardsPerPage = 5;
      rotatedCardsPerPage = 2;
    }
    const cardsPerPage = normalCardsPerPage + rotatedCardsPerPage;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename}</title>
          <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap" rel="stylesheet">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            body {
              background: #e0e0e0;
              font-family: 'DM Sans', sans-serif;
              padding: 20px;
            }

            /* Header bar */
            .print-header {
              background: #fff;
              padding: 16px 24px;
              margin-bottom: 20px;
              border-radius: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              max-width: 794px;
              margin-left: auto;
              margin-right: auto;
            }
            .print-header h1 {
              font-size: 18px;
              font-weight: 500;
              color: #333;
            }
            .print-header span {
              color: #666;
              font-size: 14px;
            }
            .print-btn {
              background: #4F7CFF;
              color: #fff;
              border: none;
              padding: 10px 24px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .print-btn:hover { background: #3d6ae8; }

            /* A4 Page Container - 210mm x 297mm */
            .a4-page {
              width: 210mm;
              min-height: 297mm;
              background: #fff;
              margin: 0 auto 20px;
              padding: 6mm;
              box-shadow: 0 4px 20px rgba(0,0,0,0.15);
              position: relative;
            }

            .page-number {
              position: absolute;
              bottom: 2mm;
              right: 6mm;
              font-size: 9px;
              color: #999;
            }

            /* Cards Grid - small gap for visible cut lines */
            .cards-grid {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              align-content: flex-start;
            }

            /* Large cards layout - two columns */
            .large-cards-layout {
              display: flex;
              gap: 6px;
              width: 100%;
              height: 100%;
            }

            .left-column {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }

            .right-column {
              display: flex;
              flex-direction: column;
              gap: 6px;
              margin-left: 0;
            }

            /* Rotated card wrapper - creates 200x500 space for rotated 500x200 card */
            .rotated-card-wrapper {
              width: 200px;
              height: 500px;
              position: relative;
              overflow: hidden;
            }

            .rotated-card-wrapper .shelf-card-large,
            .rotated-card-wrapper .shelf-card-wow {
              position: absolute;
              top: 150px;
              left: -150px;
              transform: rotate(90deg);
              transform-origin: center center;
            }

            /* Rotated compact card wrapper - creates 113x269 space for rotated 269x113 card */
            .rotated-compact-wrapper {
              width: 113px;
              height: 269px;
              position: relative;
              overflow: hidden;
            }

            .rotated-compact-wrapper .shelf-card-compact {
              position: absolute;
              top: 78px;
              left: -78px;
              transform: rotate(90deg);
              transform-origin: center center;
            }

            /* Compact cards layout - two sections */
            .compact-cards-layout {
              display: flex;
              gap: 6px;
              width: 100%;
              height: 100%;
            }

            .compact-left-section {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              width: 544px;
              align-content: flex-start;
            }

            .compact-right-section {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }

            /* Full-width horizontal cut lines */
            .cut-line-h {
              position: absolute;
              left: 0;
              right: 0;
              height: 0;
              border-top: 1px dashed #aaa;
              pointer-events: none;
            }

            /* Full-height vertical cut lines */
            .cut-line-v {
              position: absolute;
              top: 0;
              bottom: 0;
              width: 0;
              border-left: 1px dashed #aaa;
              pointer-events: none;
            }

            /* Scissors icon indicator */
            .cut-indicator {
              position: absolute;
              font-size: 10px;
              color: #999;
              background: #fff;
              padding: 0 4px;
              white-space: nowrap;
            }

            /* ===== COMPACT CARD - EXACT COPY FROM DASHBOARD ===== */
            /* Compact Card Template (269x113px) */
            .shelf-card-compact {
              width: 269px;
              height: 113px;
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              overflow: hidden;
              position: relative;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .compact-item-name {
              position: absolute;
              width: 150px;
              left: 12px;
              top: 10px;
              font-family: 'DM Sans', sans-serif;
              font-size: 13px;
              font-weight: 400;
              color: #030303;
              line-height: 1.2;
              word-wrap: break-word;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .compact-savings-section {
              position: absolute;
              right: 10px;
              top: 5px;
              text-align: right;
            }

            .compact-savings-row {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
            }

            .compact-rupee-symbol {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 600;
              font-size: 18px;
              color: #030303;
              margin-top: 5px;
              margin-right: 2px;
            }

            .compact-savings-amount {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 700;
              font-size: 44px;
              color: #030303;
              line-height: 1;
            }

            .compact-savings-text {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 700;
              font-size: 28px;
              color: black;
              line-height: 1;
              margin-top: -2px;
            }

            .compact-price-row {
              position: absolute;
              left: 12px;
              top: 52px;
              display: flex;
              align-items: baseline;
              gap: 6px;
            }

            .compact-our-price {
              font-family: 'DM Sans', sans-serif;
              font-weight: 700;
              font-style: italic;
              font-size: 14px;
              color: black;
            }

            .compact-our-price-value {
              font-family: 'DM Sans', sans-serif;
              font-weight: 700;
              font-style: italic;
              font-size: 14px;
              color: black;
            }

            .compact-mrp {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 400;
              font-size: 14px;
              color: #666;
              text-decoration: line-through;
            }

            .compact-blue-bar {
              position: absolute;
              width: 269px;
              height: 30px;
              left: 0;
              bottom: 0;
              background: linear-gradient(180deg, #5074F3 0%, #2F448D 100%);
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 12px;
            }

            .compact-loyal-logo {
              height: 22px;
              width: auto;
            }

            .compact-additional {
              display: flex;
              align-items: center;
              gap: 6px;
            }

            .additional-percent {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 700;
              font-size: 20px;
              color: white;
              line-height: 1;
            }

            .additional-text {
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 600;
              font-size: 13px;
              color: white;
              line-height: 1;
            }

            /* ===== LARGE CARD - EXACT COPY FROM DASHBOARD ===== */
            /* Large Card Template (500x200px) */
            .shelf-card-large {
              width: 500px;
              height: 200px;
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              overflow: hidden;
              display: flex;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .large-left-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              padding: 16px;
              background: #ffffff;
            }

            .large-top-row {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 12px;
            }

            .large-item-name {
              font-family: 'DM Sans', sans-serif;
              font-size: 19.48px;
              font-weight: 500;
              color: #1a1a1a;
              line-height: 1.3;
              flex: 1;
            }

            .large-price-section {
              text-align: right;
            }

            .large-current-price {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 30px;
              font-weight: 700;
              font-style: italic;
              color: #1a1a1a;
              line-height: 1;
            }

            .large-mrp-price {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 18px;
              font-weight: 500;
              font-style: italic;
              color: #9ca3af;
              text-decoration: line-through;
              line-height: 1.2;
            }

            .large-savings-display {
              display: flex;
              align-items: baseline;
              gap: 8px;
            }

            .large-rupee-savings {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 65px;
              font-weight: 700;
              font-style: italic;
              color: #1a1a1a;
              line-height: 1;
            }

            .large-savings-text {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 26px;
              font-weight: 500;
              font-style: italic;
              color: #1a1a1a;
            }

            .large-right-section {
              width: 163px;
              background: linear-gradient(180deg, #5074F3 0%, #2F448D 100%);
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 12px;
              padding: 16px;
            }

            .large-logo {
              height: 65px;
              width: auto;
            }

            .large-additional-savings {
              text-align: center;
              color: #ffffff;
            }

            .large-percent {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 48px;
              font-weight: 700;
              font-style: italic;
              display: block;
              line-height: 1;
            }

            .large-additional-text {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 22px;
              font-weight: 400;
              font-style: italic;
              display: block;
              line-height: 1.2;
            }

            /* ===== WOW WEDNESDAY CARD (500x200px - same as Medium Card) ===== */
            .shelf-card-wow {
              width: 500px;
              height: 200px;
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              overflow: hidden;
              display: flex;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .wow-left-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              padding: 16px 20px;
              background: #ffffff;
            }

            .wow-item-name {
              font-family: 'DM Sans', sans-serif;
              font-size: 18px;
              font-weight: 500;
              color: #1a1a1a;
              line-height: 1.35;
              margin-bottom: 8px;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .wow-price-row {
              display: flex;
              align-items: baseline;
              gap: 12px;
              margin-bottom: 8px;
            }

            .wow-our-price {
              font-family: 'DM Sans', sans-serif;
              font-size: 14px;
              font-weight: 700;
              color: #1a1a1a;
            }

            .wow-mrp {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 14px;
              font-weight: 400;
              font-style: italic;
              color: #9ca3af;
              text-decoration: line-through;
            }

            .wow-savings-display {
              display: flex;
              align-items: baseline;
              margin-top: auto;
            }

            .wow-rupee-symbol {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 26px;
              font-weight: 600;
              font-style: italic;
              color: #1a1a1a;
              line-height: 1;
              margin-right: 2px;
            }

            .wow-savings-amount {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 58px;
              font-weight: 700;
              font-style: italic;
              color: #1a1a1a;
              line-height: 1;
            }

            .wow-savings-text {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 24px;
              font-weight: 500;
              font-style: italic;
              color: #1a1a1a;
              margin-left: 8px;
            }

            .wow-right-section {
              width: 180px;
              display: flex;
              flex-direction: column;
              flex-shrink: 0;
            }

            .wow-wednesday-logo {
              width: 100%;
              height: auto;
              object-fit: contain;
            }

            .wow-blue-bar {
              flex: 1;
              background: linear-gradient(180deg, #4F72F0 0%, #2F4591 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              gap: 10px;
            }

            .wow-loyal-logo {
              height: 40px;
              width: auto;
              flex-shrink: 0;
            }

            .wow-divider {
              width: 1px;
              height: 50px;
              background: rgba(255, 255, 255, 0.5);
              flex-shrink: 0;
            }

            .wow-additional-section {
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .wow-percent {
              color: white;
              font-size: 32px;
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 700;
              line-height: 1;
            }

            .wow-additional-text {
              color: white;
              font-size: 12px;
              font-family: 'Barlow Condensed', sans-serif;
              font-style: italic;
              font-weight: 500;
              line-height: 1.2;
              text-align: center;
            }

            /* ===== CAFE CARD STYLES (500x200px) - Medium Size ===== */
            .cafe-card {
              width: 500px;
              height: 200px;
              background: #ffffff;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
              overflow: hidden;
              display: flex;
              position: relative;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }

            .cafe-left-section {
              flex: 1;
              padding: 16px 20px;
              display: flex;
              flex-direction: column;
              position: relative;
            }

            .cafe-item-name {
              font-family: 'DM Sans', sans-serif;
              font-size: 18px;
              font-weight: 500;
              color: #1a1a1a;
              line-height: 1.3;
              word-wrap: break-word;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              margin-bottom: 12px;
            }

            .cafe-regular-price {
              font-family: 'DM Sans', sans-serif;
              font-size: 14px;
              font-weight: 700;
              color: black;
              line-height: 1.2;
            }

            .cafe-loyal-row {
              display: flex;
              align-items: baseline;
              gap: 12px;
              margin-top: auto;
            }

            .cafe-loyal-label {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 28px;
              font-weight: 600;
              font-style: italic;
              color: #4D6FE9;
              line-height: 1;
            }

            .cafe-loyal-price {
              display: flex;
              align-items: baseline;
            }

            .cafe-rupee {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 32px;
              font-weight: 600;
              font-style: italic;
              color: #4D6FE9;
              line-height: 1;
            }

            .cafe-price-value {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 56px;
              font-weight: 700;
              font-style: italic;
              color: #4D6FE9;
              line-height: 1;
            }

            .cafe-veg-indicator {
              position: absolute;
              width: 32px;
              height: 32px;
              right: 158px;
              bottom: 8px;
              border-radius: 4px;
              border: 3px solid;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .cafe-veg-indicator.veg {
              border-color: #22BD03;
            }

            .cafe-veg-indicator.non-veg {
              border-color: #EF4444;
            }

            .veg-circle {
              width: 16px;
              height: 16px;
              background: #22BD03;
              border-radius: 9999px;
            }

            .non-veg-triangle {
              width: 0;
              height: 0;
              border-left: 8px solid transparent;
              border-right: 8px solid transparent;
              border-bottom: 14px solid #EF4444;
            }

            .cafe-right-section {
              width: 150px;
              height: 200px;
              background: #F3F3F3;
              border-left: 1px solid rgba(0, 0, 0, 0.14);
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 16px 12px;
            }

            .cafe-brand-section {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
            }

            .cafe-rapsap-logo {
              height: 28px;
              width: auto;
            }

            .cafe-label {
              display: flex;
              align-items: center;
              gap: 4px;
            }

            .cafe-coffee-icon {
              width: 20px;
              height: 20px;
            }

            .cafe-label span {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 20px;
              font-weight: 600;
              font-style: italic;
              color: black;
              line-height: 1;
            }

            .cafe-loyal-button {
              width: 120px;
              padding: 12px 8px;
              background: linear-gradient(180deg, #4F72F0 0%, #2F4591 100%);
              border-radius: 4px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 4px;
              margin-top: auto;
            }

            .cafe-loyal-button .cafe-loyal-logo {
              height: 28px;
              width: auto;
            }

            .cafe-loyal-button .loyal-subtext {
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 11px;
              font-weight: 600;
              font-style: italic;
              color: white;
              line-height: 1;
              text-align: center;
            }

            /* Print styles */
            @media print {
              @page {
                size: A4;
                margin: 0;
              }
              body {
                background: #fff;
                padding: 0;
              }
              .print-header { display: none; }
              .a4-page {
                width: 210mm;
                height: 297mm;
                padding: 6mm;
                margin: 0;
                box-shadow: none;
                page-break-after: always;
              }
              .a4-page:last-child {
                page-break-after: auto;
              }
              .page-number { display: none; }
              .compact-blue-bar,
              .large-right-section,
              .wow-blue-bar,
              .wow-wednesday-logo,
              .wow-loyal-logo,
              .cafe-card,
              .cafe-left-section,
              .cafe-right-section,
              .cafe-loyal-button,
              .cafe-loyal-logo,
              .cafe-veg-indicator,
              .veg-circle,
              .non-veg-triangle {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              /* Ensure cut lines print */
              .cut-line-h,
              .cut-line-v {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .cut-indicator {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div>
              <h1>Shelf Talkers - Print Preview</h1>
              <span>${items.length} cards · ${Math.ceil(items.length / cardsPerPage)} page(s)</span>
            </div>
            <button class="print-btn" onclick="window.print()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print PDF
            </button>
          </div>
          <div id="pages-container"></div>
          <script>
            const cardsHtml = \`${printContent.replace(/`/g, '\\`')}\`;
            const container = document.getElementById('pages-container');
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cardsHtml;
            const cards = Array.from(tempDiv.children);
            const cardsPerPage = ${cardsPerPage};
            const normalCardsPerPage = ${normalCardsPerPage};
            const rotatedCardsPerPage = ${rotatedCardsPerPage};
            const cardWidth = ${cardWidth};
            const cardHeight = ${cardHeight};
            const isCompactSize = cardWidth <= 300 && cardHeight <= 150;

            // Split cards into pages
            const pages = [];
            for (let i = 0; i < cards.length; i += cardsPerPage) {
              pages.push(cards.slice(i, i + cardsPerPage));
            }

            // Create A4 pages
            pages.forEach((pageCards, pageIndex) => {
              const page = document.createElement('div');
              page.className = 'a4-page';

              if (isCompactSize) {
                // Compact-like cards - two section layout with rotated cards on right
                const layout = document.createElement('div');
                layout.className = 'compact-cards-layout';

                // Left section: normal cards (2 columns x 9 rows)
                const leftSection = document.createElement('div');
                leftSection.className = 'compact-left-section';
                const normalCards = pageCards.slice(0, normalCardsPerPage);
                normalCards.forEach((card) => {
                  leftSection.appendChild(card.cloneNode(true));
                });
                layout.appendChild(leftSection);

                // Right section: remaining cards (rotated 90°)
                const rotatedCards = pageCards.slice(normalCardsPerPage);
                if (rotatedCards.length > 0) {
                  const rightSection = document.createElement('div');
                  rightSection.className = 'compact-right-section';
                  rotatedCards.forEach((card) => {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'rotated-compact-wrapper';
                    wrapper.appendChild(card.cloneNode(true));
                    rightSection.appendChild(wrapper);
                  });
                  layout.appendChild(rightSection);
                }

                page.appendChild(layout);

                // Add cut lines
                const gap = 6;
                const padding = 22.7;
                const leftWidth = 2 * cardWidth + gap;
                const rowCount = Math.min(Math.ceil(normalCards.length / 2), 9);

                for (let r = 1; r < rowCount; r++) {
                  const line = document.createElement('div');
                  line.className = 'cut-line-h';
                  line.style.top = (padding + r * cardHeight + (r - 1) * gap + gap / 2) + 'px';
                  if (rotatedCards.length > 0) {
                    line.style.right = 'auto';
                    line.style.width = (padding + leftWidth + gap / 2) + 'px';
                  }
                  page.appendChild(line);
                }

                const vLine1 = document.createElement('div');
                vLine1.className = 'cut-line-v';
                vLine1.style.left = (padding + cardWidth + gap / 2) + 'px';
                page.appendChild(vLine1);

                if (rotatedCards.length > 0) {
                  const vLine2 = document.createElement('div');
                  vLine2.className = 'cut-line-v';
                  vLine2.style.left = (padding + leftWidth + gap / 2) + 'px';
                  page.appendChild(vLine2);

                  for (let r = 1; r < rotatedCards.length; r++) {
                    const hLine = document.createElement('div');
                    hLine.className = 'cut-line-h';
                    hLine.style.top = (padding + r * cardWidth + (r - 1) * gap + gap / 2) + 'px';
                    hLine.style.left = (padding + leftWidth + gap) + 'px';
                    hLine.style.right = '0';
                    page.appendChild(hLine);
                  }
                }

              } else {
                // Large-like cards - two column layout with rotated cards on right
                const layout = document.createElement('div');
                layout.className = 'large-cards-layout';

                // Left column: normal cards
                const leftCol = document.createElement('div');
                leftCol.className = 'left-column';
                const normalCards = pageCards.slice(0, normalCardsPerPage);
                normalCards.forEach((card) => {
                  leftCol.appendChild(card.cloneNode(true));
                });
                layout.appendChild(leftCol);

                // Right column: remaining cards (rotated 90°)
                const rotatedCards = pageCards.slice(normalCardsPerPage);
                if (rotatedCards.length > 0) {
                  const rightCol = document.createElement('div');
                  rightCol.className = 'right-column';
                  rotatedCards.forEach((card) => {
                    const wrapper = document.createElement('div');
                    wrapper.className = 'rotated-card-wrapper';
                    // Set dynamic rotation based on card dimensions
                    wrapper.style.width = cardHeight + 'px';
                    wrapper.style.height = cardWidth + 'px';
                    const cardEl = card.cloneNode(true);
                    cardEl.style.position = 'absolute';
                    cardEl.style.top = ((cardWidth - cardHeight) / 2) + 'px';
                    cardEl.style.left = ((cardHeight - cardWidth) / 2) + 'px';
                    cardEl.style.transform = 'rotate(90deg)';
                    cardEl.style.transformOrigin = 'center center';
                    wrapper.appendChild(cardEl);
                    rightCol.appendChild(wrapper);
                  });
                  layout.appendChild(rightCol);
                }

                page.appendChild(layout);

                // Add cut lines
                const gap = 6;
                const padding = 22.7;

                for (let r = 1; r < normalCards.length; r++) {
                  const line = document.createElement('div');
                  line.className = 'cut-line-h';
                  line.style.top = (padding + r * cardHeight + (r - 1) * gap + gap / 2) + 'px';
                  if (rotatedCards.length > 0) {
                    line.style.right = 'auto';
                    line.style.width = (padding + cardWidth + gap / 2) + 'px';
                  }
                  page.appendChild(line);
                }

                if (rotatedCards.length > 0) {
                  const vLine = document.createElement('div');
                  vLine.className = 'cut-line-v';
                  vLine.style.left = (padding + cardWidth + gap / 2) + 'px';
                  page.appendChild(vLine);

                  if (rotatedCards.length > 1) {
                    const hLine = document.createElement('div');
                    hLine.className = 'cut-line-h';
                    hLine.style.top = (padding + cardWidth + gap / 2) + 'px';
                    hLine.style.left = (padding + cardWidth + gap) + 'px';
                    hLine.style.right = '0';
                    page.appendChild(hLine);
                  }
                }
              }

              // Add scissors indicator at center bottom
              const scissorsIcon = document.createElement('div');
              scissorsIcon.className = 'cut-indicator';
              scissorsIcon.style.bottom = '2mm';
              scissorsIcon.style.left = '50%';
              scissorsIcon.style.transform = 'translateX(-50%)';
              scissorsIcon.innerHTML = '✂ Cut along dotted lines';
              page.appendChild(scissorsIcon);

              const pageNum = document.createElement('div');
              pageNum.className = 'page-number';
              pageNum.textContent = 'Page ' + (pageIndex + 1) + ' of ' + pages.length;
              page.appendChild(pageNum);

              container.appendChild(page);
            });
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  // Clear all items
  const handleClear = () => {
    setItems([]);
  };

  // Get selected custom template if using one
  const selectedCustomTemplate = template?.startsWith("custom-")
    ? savedTemplates.find(t => `custom-${t.id}` === template)
    : null;

  // Determine which card component to use
  const getCardComponent = () => {
    if (selectedCustomTemplate) {
      return null; // Will use CustomCard directly
    }
    if (mode === "Cafe" || template === "cafe-card") return CafeCard;
    if (template === "template1") return CompactCard;
    if (template === "template3") return WowWednesdayCard;
    return LargeCard;
  };

  const CardComponent = getCardComponent();
  const isCafeMode = mode === "Cafe" || template === "cafe-card";
  const activeCafeTheme = cafeThemeCustom || CAFE_THEMES[cafeThemeId];
  const gridClass = isCafeMode ? "shelf-grid-cafe" : (template === "template1" ? "shelf-grid-compact" : "shelf-grid-large");

  return (
    <div className="shelf-talker-container">
      {/* Header */}
      <div className="shelf-header">
        {/* Row 1: Title, Toggle, and Dropdown */}
        <div className="shelf-header-row">
          <h1 className="shelf-title">generate instore <em>shelf talkers</em></h1>
          <div className="shelf-header-filters">
            <div className="pill-toggle">
              <button
                className={`pill-option ${mode === "Grocery" ? "active" : ""}`}
                onClick={() => handleModeChange("Grocery")}
              >
                Grocery
              </button>
              <button
                className={`pill-option ${mode === "Cafe" ? "active" : ""}`}
                onClick={() => handleModeChange("Cafe")}
              >
                Cafe
              </button>
            </div>
            {mode === "Grocery" ? (
              <Select
                value={template}
                onChange={setTemplate}
                className="template-select"
                style={{ width: 200 }}
                placeholder="Select Template"
              >
                <OptGroup label="Built-in Templates">
                  <Option value="template1">Compact Card</Option>
                  <Option value="template2">Medium Card</Option>
                  <Option value="template3">WOW Wednesday</Option>
                </OptGroup>
                {savedTemplates.length > 0 && (
                  <OptGroup label="Custom Templates">
                    {savedTemplates.map((t) => (
                      <Option key={t.id} value={`custom-${t.id}`}>
                        {t.name}
                      </Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
            ) : (
              <>
                <Select
                  value={template || "cafe-card"}
                  onChange={setTemplate}
                  className="template-select"
                  style={{ width: 200 }}
                  placeholder="Select Template"
                >
                  <OptGroup label="Built-in Templates">
                    <Option value="cafe-card">Cafe Card</Option>
                  </OptGroup>
                  {savedTemplates.length > 0 && (
                    <OptGroup label="Custom Templates">
                      {savedTemplates.map((t) => (
                        <Option key={t.id} value={`custom-${t.id}`}>
                          {t.name}
                        </Option>
                      ))}
                    </OptGroup>
                  )}
                </Select>
                {!selectedCustomTemplate && (
                  <Button
                    icon={<BgColorsOutlined />}
                    onClick={() => setShowThemePanel(!showThemePanel)}
                    type={showThemePanel ? "primary" : "default"}
                  >
                    Theme
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Row 2: Action Buttons */}
        <div className="shelf-header-actions">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={() => {
              const link = document.createElement('a');
              if (mode === "Cafe") {
                link.href = '/cafe-menu-sample.csv';
                link.download = 'cafe-menu-sample.csv';
              } else {
                link.href = '/shelf-talker-sample.csv';
                link.download = 'shelf-talker-sample.csv';
              }
              link.click();
            }}
          >
            Sample CSV
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV
          </Button>
          {canWrite() && (
            <Button
              icon={<DeleteOutlined />}
              onClick={handleClear}
              danger
              ghost
            >
              Clear
            </Button>
          )}
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            className="print-btn-black"
          >
            Print / PDF
          </Button>
        </div>
      </div>

      {/* Instructions Panel */}
      <div className="shelf-instructions">
        <div className="instructions-content">
          {mode === "Cafe" ? (
            <>
              <strong>CSV Headers:</strong> SR NO, PRODUCT NAME, MRP, LYP, CALS, VEG / NON
            </>
          ) : (
            <>
              <strong>CSV Headers:</strong> Categories, Brand, Item Name, MRP, Rapsap Price, Savings
            </>
          )}
        </div>
        <div className="instructions-tip">
          <strong>Tip:</strong> Enable "Background Graphics" in print settings for best results
        </div>
      </div>

      {/* Cafe Theme Panel */}
      {isCafeMode && !selectedCustomTemplate && showThemePanel && (
        <div className="cafe-theme-panel">
          <div className="theme-panel-header">
            <span className="theme-panel-title">Card Theme</span>
            <Button size="small" icon={<UndoOutlined />} onClick={handleResetTheme}>Reset</Button>
          </div>

          {/* Preset Swatches */}
          <div className="theme-presets-row">
            {Object.values(CAFE_THEMES).map((t) => (
              <div
                key={t.id}
                className={`theme-swatch ${cafeThemeId === t.id && !cafeThemeCustom ? 'active' : ''}`}
                onClick={() => handleCafeThemeSelect(t.id)}
              >
                <div
                  className="theme-swatch-color"
                  style={{
                    background: `linear-gradient(135deg, ${t.leftSectionBg} 50%, ${t.loyalButtonGradient[0]} 50%)`,
                    borderColor: t.cardBorder,
                  }}
                />
                <span className="theme-swatch-name">{t.name}</span>
              </div>
            ))}
          </div>

          {/* Color Pickers Grid */}
          <div className="theme-colors-grid">
            <label className="theme-color-item">
              <span>Left Bg</span>
              <input type="color" value={activeCafeTheme.leftSectionBg} onChange={(e) => handleCafeThemeCustomize('leftSectionBg', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Right Bg</span>
              <input type="color" value={activeCafeTheme.rightSectionBg} onChange={(e) => handleCafeThemeCustomize('rightSectionBg', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Border</span>
              <input type="color" value={activeCafeTheme.cardBorder} onChange={(e) => handleCafeThemeCustomize('cardBorder', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Item Name</span>
              <input type="color" value={activeCafeTheme.itemNameColor} onChange={(e) => handleCafeThemeCustomize('itemNameColor', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Loyal Price</span>
              <input type="color" value={activeCafeTheme.loyalPriceColor} onChange={(e) => handleCafeThemeCustomize('loyalPriceColor', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Loyal Button</span>
              <input type="color" value={activeCafeTheme.loyalButtonGradient[0]} onChange={(e) => handleCafeThemeCustomize('loyalButtonGradient', [e.target.value, e.target.value])} />
            </label>
          </div>

          {/* Logo Color Toggle */}
          <div className="theme-logo-toggle">
            <span className="theme-color-item"><span>Loyal Logo</span></span>
            <button
              className={`logo-toggle-btn ${(activeCafeTheme.loyalLogoFilter || 'none') === 'brightness(0) invert(1)' ? 'active' : ''}`}
              onClick={() => handleCafeThemeCustomize('loyalLogoFilter', 'brightness(0) invert(1)')}
            >White</button>
            <button
              className={`logo-toggle-btn ${(activeCafeTheme.loyalLogoFilter || 'none') === 'brightness(0)' ? 'active' : ''}`}
              onClick={() => handleCafeThemeCustomize('loyalLogoFilter', 'brightness(0)')}
            >Black</button>
            <button
              className={`logo-toggle-btn ${!activeCafeTheme.loyalLogoFilter || activeCafeTheme.loyalLogoFilter === 'none' ? 'active' : ''}`}
              onClick={() => handleCafeThemeCustomize('loyalLogoFilter', 'none')}
            >None</button>
          </div>

          {/* Offer Value */}
          <div className="theme-logo-toggle">
            <span className="theme-color-item"><span>Offer</span></span>
            <input
              type="text"
              placeholder="e.g. 7% or 25%"
              value={cafeOfferValue}
              onChange={(e) => {
                setCafeOfferValue(e.target.value);
                saveCafeTheme(cafeThemeId, cafeThemeCustom, e.target.value);
              }}
              className="theme-banner-input"
              style={{ maxWidth: 120 }}
            />
            <span style={{ fontSize: 11, color: '#999' }}>Shows as "+{cafeOfferValue || '...'} additional savings"</span>
          </div>

          {/* Banner Controls */}
          <div className="theme-banner-controls">
            <input
              type="text"
              placeholder="Banner text (e.g. Valentine's Special)"
              value={activeCafeTheme.bannerText}
              onChange={(e) => handleCafeThemeCustomize('bannerText', e.target.value)}
              className="theme-banner-input"
            />
            <label className="theme-color-item">
              <span>Bg</span>
              <input type="color" value={activeCafeTheme.bannerBg} onChange={(e) => handleCafeThemeCustomize('bannerBg', e.target.value)} />
            </label>
            <label className="theme-color-item">
              <span>Text</span>
              <input type="color" value={activeCafeTheme.bannerTextColor} onChange={(e) => handleCafeThemeCustomize('bannerTextColor', e.target.value)} />
            </label>
          </div>
        </div>
      )}

      {/* Item Count */}
      {items.length > 0 && (
        <div className="shelf-item-count">
          {items.length} item{items.length !== 1 ? "s" : ""} loaded
        </div>
      )}

      {/* Scrollable Preview Area */}
      <div className="shelf-preview-container">
        {items.length > 0 ? (
          <div ref={printAreaRef} className={`shelf-grid ${gridClass}`}>
            {items.map((item, index) =>
              selectedCustomTemplate ? (
                <CustomCard key={index} item={item} template={selectedCustomTemplate} />
              ) : (
                <CardComponent key={index} item={item} {...(isCafeMode ? { theme: activeCafeTheme, offerValue: cafeOfferValue } : {})} />
              )
            )}
          </div>
        ) : (
          <div className="shelf-empty-state">
            <div className="empty-icon">
              <UploadOutlined style={{ fontSize: 48, color: "#999" }} />
            </div>
            <p>No items to display</p>
            <p>Upload a CSV file to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
